import { useToggle } from 'ahooks'
import { Button, Col, Form, FormInstance, Modal, Row } from 'antd'
import { useAtom } from 'jotai'
import { isNil } from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { DepartmentTreeTransfer, isIdPureNumber } from '@/components/department-tree-transfer.component'
import { onDepartmentUserCheck } from '@/components/department-tree-with-users.component'
import { useOnSetSpecialProjectCache } from '@/components/special-project-local-storage-modal'
import { PriorityEnum, SpecialProjectFormLocalStorageKey } from '@/schemas'
import { useAxiosWithAtom } from '@/utils'
import { removeHtmlTag } from '@/utils/remove-html-tag'

import { departmentsTreeAtom } from '../../department'
import { DepartmentUser } from '../../project/project-create/project-form.component'
import { userInfoAtom, usersAtom } from '../../user'
import { CustomerFormPartType } from '../customer-edit.component'
import { customerAdminAtoms } from '../customer.atom'
import { Customer, OverseaRegion } from '../customer.schema'
import { isDimensionsValid } from '../customer.util'
import { CustomerBasicForm } from './customer-basic-form.component'
import { CustomerDeliveryForm } from './customer-delivery-form.component'

const validateMessages = {
  required: '${label}是必选填项!',
}

export type CustomerFromValues = {
  cycleId: number
  departmentId: number
  email: string
  name: string
  range: [string, string]
  customerType: string
  requirementTypes: string
}

export const CustomerForm: React.FC<{
  form: FormInstance<any>
  customer?: Customer
  formPartType?: CustomerFormPartType
  onSubmit: (data: CustomerFromValues) => Promise<Customer>
}> = ({ customer, form, onSubmit, formPartType }) => {
  const { t } = useTranslation()
  const [departments] = useAtom(departmentsTreeAtom)
  const [user] = useAtom(userInfoAtom)
  const [users] = useAtom(usersAtom)

  const [loading, setLoading] = React.useState(false)
  const [modalVisible, { toggle: modalToggle }] = useToggle()
  const [fieldsChanged, setFieldsChanged] = React.useState<{
    startDate: boolean
    endDate: boolean
    expectTime: boolean
    priority: boolean
    email: boolean
    businessGoal: boolean
    goal: boolean
    standard: boolean
  }>({
    startDate: false,
    endDate: false,
    expectTime: false,
    priority: false,
    email: false,
    businessGoal: false,
    goal: false,
    standard: false,
  })
  const [departmentUsers, setDepartmentUsers] = React.useState<DepartmentUser[]>([])

  const { onSetCache, setFormCache } = useOnSetSpecialProjectCache(SpecialProjectFormLocalStorageKey.CUSTOMER)

  const { data: admins } = useAxiosWithAtom(customerAdminAtoms, {
    url: '/api/customer/admins',
  })

  const readPermissions = customer?.readPermissions || []

  React.useEffect(() => {
    const defaultCheckedKeys =
      readPermissions.length > 0
        ? readPermissions
            .map((permission) => {
              // departmentId
              if (isIdPureNumber(permission)) {
                return Number(permission)
              }
              // userId
              const user = users.find((user) => user.email === permission)
              return user ? `${user.departmentIds[0]}-${user.email}` : null
            })
            .filter(Boolean)
            .map((id) => id!)
        : []
    onDepartmentUserCheck(defaultCheckedKeys, departments, users, setDepartmentUsers)
  }, [])

  React.useEffect(() => {
    if (!customer) {
      form.setFieldsValue({
        email: user.email,
        region: OverseaRegion,
        priority: PriorityEnum.P2,
      })
    } else {
      const data = {
        name: customer.name,
        email: customer.email,
        range: [
          customer.startDate ? moment(customer.startDate) : null,
          customer.endDate ? moment(customer.endDate) : null,
        ],
        background: customer.background,
        dependencies: customer.dependencies,
        businessGoal: customer.businessGoal,
        goal: customer.goal,
        priority: customer.priority,
        region: customer.region,
        expectTime: moment(customer.expectTime).isValid() ? moment(customer.expectTime) : undefined,
        standard: customer.standard,
        level: customer.level,
        startDateChangeReason: customer.startDateChangeReason
          ? removeHtmlTag(customer.startDateChangeReason)
          : customer.startDateChangeReason,
        endDateChangeReason: customer.endDateChangeReason
          ? removeHtmlTag(customer.endDateChangeReason)
          : customer.endDateChangeReason,
        expectTimeChangeReason: customer.expectTimeChangeReason
          ? removeHtmlTag(customer.expectTimeChangeReason)
          : customer.expectTimeChangeReason,
        priorityChangeReason: customer.priorityChangeReason
          ? removeHtmlTag(customer.priorityChangeReason)
          : customer.priorityChangeReason,
        businessGoalChangeReason: customer.businessGoalChangeReason
          ? removeHtmlTag(customer.businessGoalChangeReason)
          : customer.businessGoalChangeReason,
        goalChangeReason: customer.goalChangeReason
          ? removeHtmlTag(customer.goalChangeReason)
          : customer.goalChangeReason,
        standardChangeReason: customer.standardChangeReason
          ? removeHtmlTag(customer.standardChangeReason)
          : customer.standardChangeReason,
        emailChangeReason: customer.emailChangeReason
          ? removeHtmlTag(customer.emailChangeReason)
          : customer.emailChangeReason,
      }

      form.setFieldsValue(data)
    }
  }, [customer])

  const onValuesChange = (data: any) => {
    // 创建时，缓存数据
    if (!customer) {
      onSetCache(data)
    }
    const { email } = data

    if (email) {
      form.setFieldsValue({ departmentId: users.find((u) => u.email === email)?.departmentIds[0] })
    }

    // 编辑时，判断是否修改了字段
    if (customer) {
      const { range, expectTime, priority, businessGoal, email, goal, standard } = data
      if (range) {
        const oldStartDate = moment(customer?.startDate).utc()
        const oldEndDate = moment(customer?.endDate).utc()
        const newStartDate = range[0].utc()
        const newEndDate = range[1].utc()

        if (!oldStartDate.isSame(newStartDate, 'date')) {
          setFieldsChanged((prev) => ({ ...prev, startDate: true }))
        } else {
          setFieldsChanged((prev) => ({ ...prev, startDate: false }))
        }

        if (!oldEndDate.isSame(newEndDate, 'date')) {
          setFieldsChanged((prev) => ({ ...prev, endDate: true }))
        } else {
          setFieldsChanged((prev) => ({ ...prev, endDate: false }))
        }
      }

      if (!isNil(priority)) {
        setFieldsChanged((prev) => ({ ...prev, priority: customer.priority !== priority }))
      }

      // 负责人
      if (email) {
        setFieldsChanged((prev) => ({ ...prev, email: customer.email !== email }))
      }

      // 业务目标
      if (businessGoal) {
        setFieldsChanged((prev) => ({ ...prev, businessGoal: customer.businessGoal !== businessGoal }))
      }

      // 交付目标
      if (goal) {
        setFieldsChanged((prev) => ({ ...prev, goal: customer.goal !== goal }))
      }

      // 交付物&验收标准
      if (standard) {
        setFieldsChanged((prev) => ({ ...prev, standard: customer.standard !== standard }))
      }

      if (expectTime) {
        const oldExpectTime = moment(customer?.expectTime).utc()
        const newExpectTime = expectTime.utc()
        if (!oldExpectTime.isSame(newExpectTime, 'date')) {
          setFieldsChanged((prev) => ({ ...prev, expectTime: true }))
        } else {
          setFieldsChanged((prev) => ({ ...prev, expectTime: false }))
        }
      }
    }
  }

  const onFinish = () => {
    const values = form.getFieldsValue(true)

    // eslint-disable-next-line no-console
    const { range, productLines, beneficiaryAreas, fourKinds, expectTime, ...fields } = values

    if (
      isDimensionsValid(t('work.resource.productLine'), false, productLines) &&
      isDimensionsValid(t('work.resource.beneficiaryArea'), false, beneficiaryAreas) &&
      isDimensionsValid(t('work.resource.fourKind'), false, fourKinds)
    ) {
      const data = {
        ...fields,
        expectTime: expectTime.format('YYYY-MM-DD'),
        startDate: range[0].format('YYYY-MM-DD'),
        endDate: range[1].format('YYYY-MM-DD'),
      }

      setLoading(true)
      onSubmit(data).finally(() => {
        setLoading(false)
        setFormCache(undefined)
      })
    }
  }

  return (
    <>
      {!customer ? (
        <Row>
          <Col className='py-8'>
            <h2>{t('customer.createCustomer')}</h2>
          </Col>
        </Row>
      ) : null}
      <Form
        preserve={false}
        onFinish={onFinish}
        form={form}
        layout='vertical'
        validateMessages={validateMessages}
        onValuesChange={onValuesChange}
      >
        {(isNil(formPartType) || formPartType === CustomerFormPartType.BASIC) && (
          <CustomerBasicForm customer={customer} admins={admins} fieldsChanged={fieldsChanged} />
        )}
        {(isNil(formPartType) || formPartType === CustomerFormPartType.DELIVERY) && (
          <CustomerDeliveryForm customer={customer} admins={admins} fieldsChanged={fieldsChanged} />
        )}

        <Form.Item>
          <Button hidden={!!customer} loading={loading} type='primary' htmlType='submit'>
            {t('create')}
          </Button>
        </Form.Item>
      </Form>
      <Modal
        width={700}
        visible={modalVisible}
        closable={false}
        cancelButtonProps={{ style: { display: 'none' } }}
        onOk={() => modalToggle()}
        destroyOnClose
      >
        <DepartmentTreeTransfer
          users={users}
          departments={departments}
          userInfo={user}
          defaultPermissions={readPermissions}
          departmentUsers={departmentUsers}
          setDepartmentUsers={setDepartmentUsers}
        />
      </Modal>
    </>
  )
}
